import React from "react";
import { 
  Trophy, 
  Target, 
  ShieldAlert, 
  Zap, 
  Search, 
  MessageSquare, 
  ClipboardCheck,
  MousePointer2,
  ListChecks,
  AlertCircle
} from "lucide-react";

// ==========================================
// --- MINI MOCKUP COMPONENTS ---
// ==========================================

const SwotMatrixMockup = () => (
  <div className="grid grid-cols-2 gap-2 bg-gray-100 p-2 rounded-2xl border border-gray-200 shadow-inner">
    <div className="bg-emerald-50 border border-emerald-100 p-3 rounded-xl flex flex-col items-center justify-center text-center">
      <Trophy size={16} className="text-emerald-600 mb-1" />
      <span className="text-[10px] font-black text-emerald-800 uppercase">Strengths</span>
    </div>
    <div className="bg-rose-50 border border-rose-100 p-3 rounded-xl flex flex-col items-center justify-center text-center">
      <AlertCircle size={16} className="text-rose-600 mb-1" />
      <span className="text-[10px] font-black text-rose-800 uppercase">Weaknesses</span>
    </div>
    <div className="bg-blue-50 border border-blue-100 p-3 rounded-xl flex flex-col items-center justify-center text-center">
      <Zap size={16} className="text-blue-600 mb-1" />
      <span className="text-[10px] font-black text-blue-800 uppercase">Opportunities</span>
    </div>
    <div className="bg-amber-50 border border-amber-100 p-3 rounded-xl flex flex-col items-center justify-center text-center">
      <ShieldAlert size={16} className="text-amber-600 mb-1" />
      <span className="text-[10px] font-black text-amber-800 uppercase">Threats</span>
    </div>
  </div>
);

const ActionStepMockup = () => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm relative overflow-hidden">
    <div className="absolute top-0 right-0 px-2 py-1 bg-blue-600 text-[8px] font-bold text-white uppercase">Step 1</div>
    <h4 className="text-xs font-bold text-gray-900 mb-1">Improve First Step</h4>
    <p className="text-[10px] text-gray-500 leading-relaxed">Perform 3 sets of weighted box jumps daily to increase explosive power.</p>
  </div>
);

// ==========================================
// --- MAIN PAGE COMPONENT ---
// ==========================================

const SwotAnalysisPage = () => {
  return (
    <div className="space-y-12 pb-20">
      {/* Header Section */}
      <section>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight mb-4">Performance Analysis (SWOT)</h1>
        <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
          While metrics tell us how fast you are, SWOT tells us how you play. This page bridges the gap 
          between raw data and expert coaching insights.
        </p>
      </section>

      {/* 1. The Matrix Breakdown */}
      <section className="grid md:grid-cols-2 gap-12 items-center">
        <div className="space-y-5">
          <div className="flex items-center gap-2">
            <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600"><Target size={20}/></div>
            <h3 className="text-xl font-bold text-gray-900">The 4-Quadrant View</h3>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            Nuvio categorizes coach feedback into four distinct buckets. This allows athletes to 
            visualize their competitive landscape at a glance.
          </p>
          <ul className="space-y-3">
            <li className="text-sm text-gray-700"><strong>Internal:</strong> Strengths & Weaknesses (Your skills).</li>
            <li className="text-sm text-gray-700"><strong>External:</strong> Opportunities & Threats (Your environment/competition).</li>
          </ul>
        </div>
        <SwotMatrixMockup />
      </section>

      <hr className="border-gray-100" />

      {/* 2. Actionable Insights */}
      <section className="grid md:grid-cols-2 gap-12">
        <div className="order-2 md:order-1 flex flex-col justify-center gap-4">
          <ActionStepMockup />
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 flex gap-3">
            <ListChecks className="text-blue-600 flex-shrink-0" size={18} />
            <p className="text-xs text-blue-800 leading-relaxed">
              <strong>Interactive Action Steps:</strong> Each SWOT item can contain a specific "Action Plan" 
              written by your coach. These act as your micro-goals for the week.
            </p>
          </div>
        </div>
        <div className="order-1 md:order-2 space-y-5">
          <div className="flex items-center gap-2">
            <div className="bg-blue-100 p-2 rounded-lg text-blue-600"><ClipboardCheck size={20}/></div>
            <h3 className="text-xl font-bold text-gray-900">Actionable Coaching</h3>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            Feedback is useless without a plan. Our system allows coaches to attach a structured 
            <strong> Action Plan</strong> to any observation. 
          </p>
          <div className="space-y-2 text-sm text-gray-600">
             <div className="flex items-center gap-2 italic">
                <MousePointer2 size={14} className="text-gray-400" />
                <span>Click any card to reveal the Coach's step-by-step instructions.</span>
             </div>
          </div>
        </div>
      </section>

      {/* 3. Multi-Program Evaluation */}
      <section className="bg-gray-900 rounded-3xl p-8 text-white">
        <div className="flex flex-col md:flex-row gap-8 items-center">
          <div className="flex-1 space-y-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="text-purple-400" />
              <h3 className="text-2xl font-bold">Consolidated Feedback</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Athletes often play for multiple teams. The SWOT page features a 
              <strong> Program Selector</strong> that lets you toggle between evaluations from 
              different coaching staffs (e.g., High School vs. AAU).
            </p>
          </div>
          <div className="flex-shrink-0 bg-slate-800 p-4 rounded-2xl border border-slate-700">
             <div className="flex gap-2">
                <div className="px-3 py-1 bg-blue-600 rounded-md text-[10px] font-bold">HS Varsity</div>
                <div className="px-3 py-1 bg-slate-700 rounded-md text-[10px] font-bold text-slate-400">Elite Academy</div>
             </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SwotAnalysisPage;